import React from 'react';
import { Container, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <>
      <SEO 
        title="Privacy Policy - IT World"
        description="Learn how IT World collects, uses, and protects your personal information."
      />
      <div className="bg-primary text-white py-5">
        <Container>
          <h1 className="text-center mb-3">Privacy Policy</h1>
          <p className="text-center lead">Last Updated: February 2025</p>
        </Container>
      </div>

      <Container className="py-5">
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4 p-md-5">
            <section className="mb-5">
              <h2 className="h4 mb-3">1. Introduction</h2>
              <p>
                IT World ("we", "us", or "our"), operating through www.accountssuitpro.com, is 
                committed to protecting your privacy. This Privacy Policy explains how we collect, 
                use, disclose, and safeguard your information when you visit our website and use 
                our services.
              </p>
              <p>
                Please read this policy carefully. By using our services, you consent to the 
                practices described in this Privacy Policy.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">2. Information We Collect</h2>
              <h5>2.1 Personal Information</h5>
              <p>We may collect the following personal information:</p>
              <ul>
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Billing address</li>
                <li>Payment information (processed securely through Razorpay)</li>
              </ul>

              <h5 className="mt-4">2.2 Account Information</h5>
              <p>When you create an account, we collect:</p>
              <ul>
                <li>Username/Email</li>
                <li>Password (encrypted)</li>
                <li>Order history</li>
                <li>License key records</li>
              </ul>

              <h5 className="mt-4">2.3 Automatically Collected Information</h5>
              <p>When you visit our website, we may automatically collect:</p>
              <ul>
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>Pages visited and time spent</li>
                <li>Referring website</li>
                <li>Cookies and similar technologies</li>
              </ul>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">3. How We Use Your Information</h2>
              <p>We use the collected information for the following purposes:</p>
              <ul>
                <li>To process and fulfill your orders</li>
                <li>To deliver license keys and order confirmations</li>
                <li>To create and manage your account</li>
                <li>To provide customer support</li>
                <li>To send transactional emails (order updates, receipts)</li>
                <li>To improve our website and services</li>
                <li>To detect and prevent fraud</li>
                <li>To comply with legal obligations</li>
                <li>To send promotional communications (with your consent)</li>
              </ul>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">4. Payment Processing</h2>
              <p>
                We use Razorpay as our payment gateway. When you make a payment, your payment 
                information is collected and processed directly by Razorpay. We do not store 
                your complete credit card or debit card details on our servers.
              </p>
              <p>
                Razorpay's privacy policy governs the collection and use of your payment 
                information. Please review their privacy policy for more details.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">5. Information Sharing</h2>
              <p>We may share your information with:</p>
              <ul>
                <li>
                  <strong>Payment Processors:</strong> Razorpay, for processing payments securely
                </li>
                <li>
                  <strong>Service Providers:</strong> Third-party services that help us operate 
                  our business (email services, analytics)
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law or to protect our rights
                </li>
              </ul>
              <p className="mt-3">
                We do NOT sell, rent, or trade your personal information to third parties for 
                marketing purposes.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">6. Cookies</h2>
              <p>
                We use cookies and similar technologies to enhance your browsing experience. 
                Cookies are small files stored on your device that help us:
              </p>
              <ul>
                <li>Remember your preferences</li>
                <li>Keep you logged in</li>
                <li>Understand how you use our website</li>
                <li>Improve our services</li>
              </ul>
              <p>
                You can control cookies through your browser settings. However, disabling cookies 
                may affect some features of our website.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">7. Data Security</h2>
              <p>
                We implement appropriate technical and organizational security measures to protect 
                your personal information, including:
              </p>
              <ul>
                <li>SSL encryption for data transmission</li>
                <li>Secure password hashing</li>
                <li>Regular security assessments</li>
                <li>Limited access to personal data</li>
              </ul>
              <p>
                However, no method of transmission over the Internet is 100% secure, and we cannot 
                guarantee absolute security.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">8. Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to fulfill the 
                purposes outlined in this policy, unless a longer retention period is required 
                by law. Order records and license key information may be retained for warranty 
                and support purposes.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">9. Your Rights</h2>
              <p>You have the right to:</p>
              <ul>
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your account and data</li>
                <li>Opt-out of marketing communications</li>
                <li>Request a copy of your data</li>
              </ul>
              <p>
                To exercise these rights, please contact us at{' '}
                <a href="mailto:itworldwani@gmail.com">itworldwani@gmail.com</a>.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">10. Children's Privacy</h2>
              <p>
                Our services are not intended for children under 18 years of age. We do not 
                knowingly collect personal information from children. If you believe we have 
                collected information from a child, please contact us immediately.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">11. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites. We are not responsible 
                for the privacy practices of these websites. We encourage you to read the 
                privacy policies of any third-party sites you visit.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted on 
                this page with an updated "Last Updated" date. We encourage you to review this 
                policy periodically.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">13. Contact Us</h2>
              <p>
                If you have any questions or concerns about this Privacy Policy or our data 
                practices, please contact us:
              </p>
              <ul className="list-unstyled">
                <li><strong>Business Name:</strong> IT World</li>
                <li><strong>Email:</strong> <a href="mailto:itworldwani@gmail.com">itworldwani@gmail.com</a></li>
                <li><strong>Phone:</strong> +91 9561306384</li>
                <li><strong>Website:</strong> www.accountssuitpro.com</li>
              </ul>
            </section>

            <hr className="my-5" />

            <div className="text-center">
              <p className="text-muted mb-3">
                By using IT World services, you acknowledge that you have read and understood 
                this Privacy Policy.
              </p>
              <Link to="/contact" className="btn btn-primary me-2">
                Contact Us
              </Link>
              <Link to="/terms" className="btn btn-outline-primary">
                Terms of Use
              </Link>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default PrivacyPolicy;
